/*
  JILZARAH — Halloween 2026 landing page behaviour (CAMPAIGN layer; sunset: after Halloween 2026)

  Three independent widgets, each initialised from its own section root so the Theme Editor
  can reload one section without touching the others, and re-initialised safely (a section
  that is already wired is skipped; a reloaded section is a new element, so it wires fresh).

    [data-hw-hero]   (flashlight is the shared jilzarah-flashlight component)
    [data-hw-shop]   a quantity stepper under each portrait → one /cart/add.js call
                     with an `items` array, then the theme's own cart refresh
    [data-hw-cast]   back-of-card hotspots (desktop) / bead grid (phone) → one detail panel

  Everything the shop widget needs about the products (variant id, price in cents,
  availability) is rendered by Liquid into a JSON block inside the section, so nothing is
  hardcoded here. Money is formatted with the shop's own money format, also passed in.

  No dependencies. Vanilla DOM only.
*/
(function () {
  'use strict';

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var COLORS = ['#f18449', '#d8b5dd', '#d3df8e', '#e9989f']; /* add-to-bag burst */

  function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
  function once(el) { if (!el || el.dataset.hwWired) return false; el.dataset.hwWired = '1'; return true; }

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var out = (format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString());
    /* the homepage prints whole-dollar prices without the .00; match it */
    return out.replace(/([.,]00)(?!\d)/, '');
  }

  /* ---------- scroll-in reveal ---------- */
  function wireReveal(root) {
    var items = root.querySelectorAll('[data-hw-reveal]');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion) { items.forEach(function (el) { el.classList.add('jz-hw-visible'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('jz-hw-visible'); io.unobserve(e.target); } });
    }, { threshold: 0.1 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 1. hero ---------- */
  /* The flashlight is the shared component (assets/jilzarah-flashlight.js); here only the reveal. */
  function wireHero(root) {
    if (!once(root)) return;
    root.classList.add('has-js');
    wireReveal(root);
  }

  /* ---------- 2. direct order ---------- */
  function wireShop(root) {
    if (!once(root)) return;
    root.classList.add('has-js');
    var cfgEl = root.querySelector('[data-hw-config]');
    if (!cfgEl) return;
    var cfg; try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var products = cfg.products || [];
    var byKey = {}; products.forEach(function (p) { byKey[p.key] = p; });
    var qty = {}; products.forEach(function (p) { qty[p.key] = p.available ? 1 : 0; });
    var MAX = 9;

    var addBtn = root.querySelector('[data-hw-add]');
    var addWrap = root.querySelector('[data-hw-add-wrap]');
    var status = root.querySelector('[data-hw-status]');
    var totalEl = root.querySelector('[data-hw-total]');
    var countEl = root.querySelector('[data-hw-item-count]');
    var busy = false;

    function money(c) { return formatMoney(c, cfg.moneyFormat); }
    /* `was` is the same set at regular prices: while the products are on sale (compare-at
       above the price) the total shows it crossed out, and stops by itself when the sale ends */
    function totals() {
      var items = 0, cents = 0, was = 0;
      products.forEach(function (p) { items += qty[p.key]; cents += qty[p.key] * p.price; was += qty[p.key] * Math.max(p.price, p.compareAt || 0); });
      return { items: items, cents: cents, was: was };
    }
    function render() {
      var t = totals();
      products.forEach(function (p) {
        var n = qty[p.key], active = n > 0;
        var count = root.querySelector('[data-hw-count="' + p.key + '"]'); if (count) count.textContent = n;
        /* the photo only REFLECTS the stepper (dims at 0); it is not a control */
        var portrait = root.querySelector('[data-hw-portrait="' + p.key + '"]');
        if (portrait) portrait.setAttribute('data-hw-active', active ? 'true' : 'false');
        var state = root.querySelector('[data-hw-state="' + p.key + '"]');
        if (state) state.textContent = !p.available ? cfg.strings.soldOut : (active ? (n > 1 ? n + ' ' + cfg.strings.inSetN : cfg.strings.inSet) : cfg.strings.notInSet);
        var minus = root.querySelector('[data-hw-step="-1"][data-hw-target="' + p.key + '"]');
        var plus = root.querySelector('[data-hw-step="1"][data-hw-target="' + p.key + '"]');
        if (minus) minus.disabled = !p.available || n === 0;
        if (plus) plus.disabled = !p.available || n >= MAX;
      });
      if (countEl) countEl.textContent = t.items;
      if (totalEl) totalEl.innerHTML = t.was > t.cents
        ? '<span class="visually-hidden">Regular price</span><del>' + money(t.was) + '</del> <span class="visually-hidden">Sale price</span>' + money(t.cents)
        : money(t.cents);
      if (addBtn) {
        addBtn.disabled = t.items === 0 || busy;
        addBtn.classList.remove('is-added');
        addBtn.textContent = t.items === 0 ? cfg.strings.selectOne : cfg.strings.add + ' · ' + money(t.cents);
      }
    }

    root.addEventListener('click', function (ev) {
      var step = ev.target.closest('[data-hw-step]');
      if (step && root.contains(step)) {
        var key = step.dataset.hwTarget, prod = byKey[key];
        if (!prod || !prod.available) return;
        qty[key] = Math.max(0, Math.min(MAX, qty[key] + Number(step.dataset.hwStep)));
        setStatus('');
        render();
      }
    });

    function setStatus(html, isError) {
      if (!status) return;
      status.innerHTML = html;
      status.classList.toggle('is-error', !!isError);
    }
    function burst() {
      if (reducedMotion || !addWrap) return;
      for (var i = 0; i < 16; i++) {
        var b = document.createElement('span');
        var angle = (Math.PI * 2 * i) / 16, dist = 48 + (i % 5) * 13;
        b.className = 'jz-hw-burst';
        b.style.setProperty('--hw-burst-x', Math.cos(angle) * dist + 'px');
        b.style.setProperty('--hw-burst-y', Math.sin(angle) * dist + 'px');
        b.style.setProperty('--hw-burst-color', COLORS[i % COLORS.length]);
        addWrap.appendChild(b);
        (function (el) { setTimeout(function () { el.remove(); }, 800); })(b);
      }
    }
    function themeCartRefresh() {
      /* Flex 6.0.0: PXUTheme.jsAjaxCart owns the header count, mini cart and drawer */
      var ac = window.PXUTheme && window.PXUTheme.jsAjaxCart;
      if (!ac) return false;
      try { if (typeof ac.updateView === 'function') ac.updateView(); } catch (e) {}
      try { if (ac.cart_action === 'drawer' && typeof ac.showDrawer === 'function') { ac.showDrawer(); return true; } } catch (e) {}
      return false;
    }
    function rootPath() { var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; return r.replace(/\/?$/, '/'); }

    if (addBtn) addBtn.addEventListener('click', function () {
      var t = totals();
      if (!t.items || busy) return;
      var items = products.filter(function (p) { return qty[p.key] > 0; }).map(function (p) { return { id: p.variantId, quantity: qty[p.key] }; });
      busy = true; addBtn.disabled = true; addBtn.classList.add('is-busy'); setStatus(cfg.strings.adding);
      fetch(rootPath() + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          busy = false; addBtn.classList.remove('is-busy');
          if (!res.ok) {
            var d = res.body && (res.body.description || res.body.message);
            setStatus(typeof d === 'string' && d ? d : cfg.strings.error, true);
            render();
            return;
          }
          var drawerOpened = themeCartRefresh();
          burst();
          addBtn.classList.add('is-added');
          addBtn.textContent = cfg.strings.added;
          var n = t.items;
          var msg = n + ' ' + (n === 1 ? cfg.strings.bracelet : cfg.strings.bracelets) + ' ' + cfg.strings.addedToBag;
          if (!drawerOpened) msg += ' <a href="' + cfg.cartUrl + '">' + cfg.strings.viewBag + '</a>';
          setStatus(msg);
          setTimeout(function () { render(); setStatus(msg); }, 1900);
        })
        .catch(function () { busy = false; addBtn.classList.remove('is-busy'); setStatus(cfg.strings.network, true); render(); });
    });

    render();
    wireReveal(root);
  }

  /* ---------- 3. bead explorer ---------- */
  function wireCast(root) {
    if (!once(root)) return;
    root.classList.add('has-js');
    var buttons = root.querySelectorAll('[data-hw-bead]');
    var img = root.querySelector('[data-hw-focus-img]');
    var name = root.querySelector('[data-hw-focus-name]');
    var copy = root.querySelector('[data-hw-focus-copy]');
    if (!buttons.length) return;

    function select(btn) {
      var key = btn.dataset.hwBead;
      buttons.forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.hwBead === key ? 'true' : 'false'); });
      if (img && btn.dataset.hwImage) { img.src = btn.dataset.hwImage; img.alt = btn.dataset.hwName ? btn.dataset.hwName + ' bead' : ''; }
      if (name) name.textContent = btn.dataset.hwName || '';
      if (copy) copy.textContent = btn.dataset.hwCopy || '';
      if (img && !reducedMotion) { img.classList.remove('is-popping'); void img.offsetWidth; img.classList.add('is-popping'); }
    }
    root.addEventListener('click', function (ev) {
      var b = ev.target.closest('[data-hw-bead]');
      if (b && root.contains(b)) select(b);
    });
    wireReveal(root);
  }

  /* ---------- boot ---------- */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-hw-hero]').forEach(wireHero);
    (scope || document).querySelectorAll('[data-hw-shop]').forEach(wireShop);
    (scope || document).querySelectorAll('[data-hw-cast]').forEach(wireCast);
    (scope || document).querySelectorAll('[data-hw-close]').forEach(function (el) { if (once(el)) { el.classList.add('has-js'); wireReveal(el); } });
  }
  ready(function () { boot(document); });
  /* Theme Editor: a reloaded section is a fresh element; wire just that one */
  document.addEventListener('shopify:section:load', function (ev) { boot(ev.target); });
})();
